"use client";
import type { ICountdownTimer } from "@contentful-api/types/contentful";
import { Link } from "@components/cms/link/Link";
import { RichText } from "@components/cms/rich-text-renderer/RichText";
import { Box } from "@ui/components/layout/box/Box";
import { Flex } from "@ui/components/layout/flex/Flex";
import { Button } from "@ui/components/forms/button/Button";
import { useEffect, useState } from "react";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { CommonCMS } from "@lib/constants/contentful";
import { useStoreApi } from "@store/store";

interface CountdownTimerProps {
    countdownTimer?: ICountdownTimer;
}

interface TimeLeft {
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
}

const calculateTimeLeft = (endDate: string | Date): TimeLeft | null => {
    const diff = new Date(endDate).getTime() - Date.now();

    if (diff <= 0) {
        return { days: "00", hours: "00", minutes: "00", seconds: "00" };
    }

    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    return {
        days: String(days).padStart(2, "0"),
        hours: String(hours).padStart(2, "0"),
        minutes: String(minutes).padStart(2, "0"),
        seconds: String(seconds).padStart(2, "0"),
    };
};

export const CountdownTimer = ({ countdownTimer }: CountdownTimerProps) => {
    const store = useStoreApi();
    const { promotionalText, promotionalButton, color } = countdownTimer.fields;
    const endDate24h = countdownTimer?.fields?.setEndDate24h;
    const endDateAmPm = countdownTimer?.fields?.setEndDateAmPm;
    // US/CA use AM/PM format, other markets use 24h format. If both are set, 24h takes precedence as discussed.
    const endDate = endDate24h || endDateAmPm;
    const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(() =>
        endDate ? calculateTimeLeft(endDate) : null
    );

    const { getMultiple } = useMicrocopy();
    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.countdownTimer.days,
        CommonCMS.countdownTimer.hours,
        CommonCMS.countdownTimer.minutes,
        CommonCMS.countdownTimer.seconds,
    ]);

    useEffect(() => {
        if (!endDate) {
            // No timer data, mark as inactive
            store.getState().setHasActiveTimer(false);
            return;
        }

        // Timer exists and has valid data, mark as active
        store.getState().setHasActiveTimer(true);

        // Calculate immediately to avoid flash
        setTimeLeft(calculateTimeLeft(endDate));

        const timer = setInterval(() => {
            const newTimeLeft = calculateTimeLeft(endDate);
            setTimeLeft(newTimeLeft);

            // Stop interval and update store when countdown ends
            if (newTimeLeft && Object.values(newTimeLeft).every((val) => val === "00")) {
                clearInterval(timer);
                store.getState().setHasActiveTimer(false);
            }
        }, 1000);

        return () => {
            clearInterval(timer);
            // Clean up: mark as inactive when component unmounts
            store.getState().setHasActiveTimer(false);
        };
    }, [endDate, store]);

    // Check if countdown has ended (all zeros)
    if (
        !countdownTimer?.fields ||
        (timeLeft && Object.values(timeLeft).every((val) => val === "00"))
    ) {
        return null;
    }

    // Don't render until timeLeft is calculated
    if (!timeLeft) {
        return null;
    }

    const timeUnits = [
        { value: timeLeft.days, label: microcopies[CommonCMS.countdownTimer.days] },
        { value: timeLeft.hours, label: microcopies[CommonCMS.countdownTimer.hours] },
        { value: timeLeft.minutes, label: microcopies[CommonCMS.countdownTimer.minutes] },
        { value: timeLeft.seconds, label: microcopies[CommonCMS.countdownTimer.seconds] },
    ];

    return (
        <Box
            w="full"
            h={["60px", "70px"]}
            py={[0.5, 2]}
            px={[0, 2]}
            backgroundColor={color}
            position={"sticky"}
            top={0}
            zIndex={11}
        >
            <Flex
                alignItems="center"
                justifyContent="center"
                gap={[1, 4]}
                flexDirection={["column", "row"]}
            >
                <Flex
                    flex={1}
                    justifyContent={["center", "center"]}
                    color="white"
                    alignItems="center"
                    fontSize={["sm", "md"]}
                    textAlign="center"
                    width={["100%", "auto"]}
                    sx={{
                        "& p": {
                            marginBottom: 0,
                            width: "100%",
                        },
                    }}
                >
                    {promotionalText && <RichText data={promotionalText} />}
                </Flex>

                <Flex gap={[1, 4]} color="white" fontWeight="bold" alignItems="flex-start">
                    {timeUnits.map((unit, index) => (
                        <Flex key={unit.label} alignItems="flex-start" gap={[1, 4]}>
                            <Box textAlign="center">
                                <Box fontSize={["md", "2xl"]}>{unit.value}</Box>
                                <Box fontSize={["3xs", "xs"]} fontWeight="normal" opacity={0.6}>
                                    {unit.label}
                                </Box>
                            </Box>
                            {index < timeUnits.length - 1 && (
                                <Box fontSize={["md", "2xl"]} opacity={0.6}>
                                    :
                                </Box>
                            )}
                        </Flex>
                    ))}
                </Flex>

                <Flex
                    flex={1}
                    justifyContent={["center", "center"]}
                    alignItems="center"
                    display={["none", "flex"]}
                >
                    {promotionalButton?.fields && (
                        <Button
                            as={Link}
                            link={promotionalButton.fields.link}
                            variant="standard"
                            size="sm"
                            colorScheme={
                                promotionalButton.fields.type === "solid" ? "black" : "white"
                            }
                        >
                            {promotionalButton.fields.label}
                        </Button>
                    )}
                </Flex>
            </Flex>
        </Box>
    );
};
